<template>
  <a-modal title="Chat umbenennen" v-model:open="renameModalVisible" okText="Speichern" @ok="renameCase()">
    <a-input v-model:value="caseNewName" placeholder="Basic usage" />
  </a-modal>

  <div v-if="collapsed" class="llm_doc_browser_collapsed">
    <a-tooltip title="Zum Öffnen darüberfahren" placement="right">
      <RightOutlined class="llm_collapsed_hint" />
    </a-tooltip>
  </div>

  <div v-else class="llm_doc_browser">
    <div class="llm_sidebar_toolbar">
      <a-input v-model:value="searchQuery" placeholder="Chats durchsuchen" allow-clear class="llm_sidebar_search" @press-enter="onSearchSubmit">
        <template #prefix>
          <SearchOutlined style="color: rgba(0, 0, 0, 0.35)" />
        </template>
      </a-input>

      <a-button v-if="canFilterByUser" class="llm_sidebar_date_btn" :type="userPickerOpen || !!selectedUser ? 'primary' : 'default'" @click="userPickerOpen = !userPickerOpen">
        <template #icon><UserOutlined /></template>
      </a-button>
      <a-button class="llm_sidebar_date_btn" :type="datePickerOpen ? 'primary' : 'default'" @click="datePickerOpen = !datePickerOpen">
        <template #icon><CalendarOutlined /></template>
      </a-button>
    </div>

    <div v-if="userPickerOpen" class="llm_sidebar_date_row">
      <a-select
        v-model:value="selectedUser"
        show-search
        allow-clear
        placeholder="Benutzer auswählen"
        class="llm_sidebar_user_select"
        :options="userOptions"
        :filter-option="filterUserOption"
        :loading="usersLoading"
        @change="onUserFilterChange"
      />
    </div>

    <div v-if="datePickerOpen" class="llm_sidebar_date_row">
      <a-range-picker
        v-model:value="rangePickerValue"
        :presets="datePresets"
        :allow-empty="[true, true]"
        format="DD.MM.YYYY"
        :placeholder="['Von', 'Bis']"
        class="llm_sidebar_range_picker"
        @change="onRangePickerChange"
      />
    </div>

    <div class="llm_sidebar_body">
      <template v-if="hasResults">
        <div v-for="(group, groupKey) of cases" :key="groupKey" class="llm_group">
          <div class="llm_group_header" @click="toggleGroup(String(groupKey))">
            <span class="llm_group_label">{{ formatGroupLabel(String(groupKey)) }}</span>
            <DownOutlined v-if="!isGroupCollapsed(String(groupKey))" class="llm_group_chevron" />
            <RightOutlined v-else class="llm_group_chevron" />
          </div>

          <div v-if="!isGroupCollapsed(String(groupKey))" class="llm_group_items">
            <div v-for="item of group" :key="item.id" class="llm_case_item" :class="{ llm_case_item_selected: selectedKeys.includes(item.id) }" @click="onMenuItemSelected({ key: item.id })">
              <component :is="iconForItem(item)" class="llm_case_icon" />
              <div class="llm_case_content">
                <div class="llm_case_title">{{ item.name }}</div>
                <a-tooltip :title="formatAbsoluteDate(item.creation_date)" placement="top">
                  <div class="llm_case_date">{{ formatRelativeDate(item.creation_date) }}</div>
                </a-tooltip>
              </div>
              <span class="llm_case_actions" @click.stop>
                <a-dropdown :trigger="['click']" placement="bottomRight">
                  <a-button type="text" size="small" class="llm_case_action_btn" @click.stop>
                    <MoreOutlined />
                  </a-button>
                  <template #overlay>
                    <a-menu @click="(e: any) => e.domEvent && e.domEvent.stopPropagation()">
                      <a-menu-item key="rename" @click="openRenameDialog(item.id, item.name)"> <EditOutlined /> Umbenennen </a-menu-item>
                      <a-menu-item key="export" @click="eventBus.emit('on-case-export', { id: item.id })"> <DownloadOutlined /> Exportieren </a-menu-item>
                      <a-menu-divider />
                      <a-popconfirm
                        title="Diesen Chat löschen?"
                        ok-text="Löschen"
                        cancel-text="Abbrechen"
                        ok-type="danger"
                        placement="left"
                        @confirm="eventBus.emit('on-case-delete', { id: item.id })"
                      >
                        <a-menu-item key="delete" danger @click.stop> <DeleteOutlined /> Löschen </a-menu-item>
                      </a-popconfirm>
                    </a-menu>
                  </template>
                </a-dropdown>
              </span>
            </div>
          </div>
        </div>
      </template>

      <a-empty v-else-if="!loading" :description="hasActiveFilter ? 'Keine passenden Chats' : 'Noch keine Chats'" style="margin-top: 24px" />

      <a-button v-if="hasResults && pagination.current < pagination.total" type="text" class="llm_load_more" @click="loadMoreCases"> Mehr laden </a-button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onUnmounted, reactive, ref, toRefs, watch, VNode } from "vue";
import {
  EditOutlined,
  DownloadOutlined,
  DeleteOutlined,
  CalendarOutlined,
  MoreOutlined,
  SearchOutlined,
  MessageOutlined,
  FileTextOutlined,
  NodeIndexOutlined,
  DownOutlined,
  RightOutlined,
  UserOutlined,
} from "@ant-design/icons-vue";
import type { LLMCaseItem } from "../../models";
import { getFilterValues } from "../../api/documentReport";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { debounce } from "lodash-es";
import dayjs, { Dayjs } from "dayjs";

import { updateCase } from "../../api/cases";
import eventBus from "../workflow/eventBus";
import { useLLMCaseListStore, LLMCaseListFilter } from "../../store/modules/llmCaseList";
import { useUserStore } from "../../store/modules/user";
import { formatAbsoluteDate, formatRelativeDate } from "../../utils/timeUtils";

interface State {
  selectedKeys: string[];
  collapsedGroups: Record<string, boolean>;
  renameModalVisible: boolean;
  renamingCaseId: string;
  caseNewName: string;
  loading: boolean;
}

export default defineComponent({
  components: {
    EditOutlined,
    DownloadOutlined,
    DeleteOutlined,
    CalendarOutlined,
    MoreOutlined,
    SearchOutlined,
    MessageOutlined,
    FileTextOutlined,
    NodeIndexOutlined,
    DownOutlined,
    RightOutlined,
    UserOutlined,
  },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const store = useLLMCaseListStore();
    const { cases, pagination } = storeToRefs(store);
    const { loadMoreCases } = store;
    const userStore = useUserStore();

    const canFilterByUser = computed(() => {
      const role = userStore.profile?.role;
      return role === "ROLE_TENANTADMIN" || role === "ROLE_ADMIN" || "ROLE_TENANTADMIN_RESTRICTED";
    });

    const users = ref<string[]>([]);
    const usersLoading = ref(false);
    const userPickerOpen = ref(false);
    const selectedUser = ref<string | undefined>(undefined);
    const userOptions = computed(() => users.value.map((u) => ({ value: u, label: u })));
    const filterUserOption = (input: string, option: { label?: string }) => (option.label ?? "").toLowerCase().includes(input.toLowerCase());
    const loadUsers = async () => {
      if (!canFilterByUser.value || users.value.length > 0 || usersLoading.value) return;
      usersLoading.value = true;
      try {
        const filterValues = await getFilterValues("cases");
        const creatorField = filterValues.find((f) => f.field === "creator");
        users.value = (creatorField?.values ?? []).filter((v): v is string => !!v).sort();
      } catch {
        users.value = [];
      } finally {
        usersLoading.value = false;
      }
    };
    watch(userPickerOpen, (open) => {
      if (open) loadUsers();
    });

    const state = reactive<State>({
      selectedKeys: [],
      collapsedGroups: {},
      renameModalVisible: false,
      renamingCaseId: "",
      caseNewName: "",
      loading: false,
    });

    const searchQuery = ref("");
    const rangePickerValue = ref<[Dayjs, Dayjs] | null>([dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")]);
    const datePickerOpen = ref(false);

    const datePresets = [
      { label: "Heute", value: [dayjs().startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Letzte Woche", value: [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Letzter Monat", value: [dayjs().subtract(30, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
      { label: "Aktuelles Jahr", value: [dayjs().startOf("year"), dayjs().endOf("year")] as [Dayjs, Dayjs] },
    ];

    const hasResults = computed(() => Object.keys(cases.value).length > 0);
    const flatCases = computed<LLMCaseItem[]>(() => Object.values(cases.value).reduce<LLMCaseItem[]>((acc, group) => acc.concat(group), []));

    const iconForItem = (item: LLMCaseItem) => {
      if (item.hasWorkflow) return NodeIndexOutlined;
      if (item.hasDocuments) return FileTextOutlined;
      return MessageOutlined;
    };
    const hasActiveDateFilter = computed(() => rangePickerValue.value !== null);
    const hasActiveFilter = computed(() => searchQuery.value.trim().length > 0 || hasActiveDateFilter.value);

    const buildFilter = (): LLMCaseListFilter => {
      const filter: LLMCaseListFilter = {};
      const trimmed = searchQuery.value.trim();
      if (trimmed) filter.name = trimmed;
      if (rangePickerValue.value) {
        const [start, end] = rangePickerValue.value;
        filter.fromDate = start.startOf("day").toDate().toISOString();
        filter.toDate = end.endOf("day").toDate().toISOString();
      }
      if (selectedUser.value) filter.creator = selectedUser.value;
      return filter;
    };

    const applyFilters = async () => {
      state.loading = true;
      try {
        await store.updateCaseList(buildFilter());
      } finally {
        state.loading = false;
      }
    };

    const debouncedApply = debounce(applyFilters, 300);

    watch(searchQuery, () => {
      debouncedApply();
    });

    const onSearchSubmit = () => {
      debouncedApply.cancel();
      applyFilters();
    };
    const onRangePickerChange = (value: [Dayjs, Dayjs] | null) => {
      const cleared = !value || (!value[0] && !value[1]);
      if (cleared) {
        rangePickerValue.value = null;
        datePickerOpen.value = false;
      }
      applyFilters();
    };
    const onUserFilterChange = (value?: string) => {
      if (!value) {
        selectedUser.value = undefined;
        userPickerOpen.value = false;
      }
      applyFilters();
    };

    const router = useRouter();
    const removeAfterEach = router.afterEach((to, from) => {
      if (to.name === "Large Language Model" && from.name !== "Large Language Model") {
        applyFilters();
      }
    });

    onMounted(() => {
      applyFilters();
    });

    onUnmounted(() => {
      removeAfterEach();
      debouncedApply.cancel();
    });

    const route = useRoute();
    watch(
      () => route.params,
      () => {
        if (route.params["caseId"] && (route.params["caseId"] as string).length > 8) {
          state.selectedKeys = [route.params["caseId"] as string];
        } else {
          state.selectedKeys = [];
        }
      },
      { deep: true }
    );

    const containerRef = ref<VNode | null>(null);

    const onMenuItemSelected = ({ key }: { key: string }) => {
      eventBus.emit("on-case-selected", { id: key, interuptProgress: true });
    };

    const openRenameDialog = (caseId: string, curName: string) => {
      state.renameModalVisible = true;
      state.renamingCaseId = caseId;
      state.caseNewName = curName;
    };

    const renameCase = async () => {
      try {
        await updateCase({ id: state.renamingCaseId, title: state.caseNewName, type: "llm" });
        state.renamingCaseId = "";
        state.renameModalVisible = false;
        await applyFilters();
        return Promise.resolve();
      } catch {
        return Promise.reject();
      }
    };

    const formatGroupLabel = (key: string) => key.toUpperCase();
    const isGroupCollapsed = (key: string) => state.collapsedGroups[key] === true;
    const toggleGroup = (key: string) => {
      state.collapsedGroups[key] = !state.collapsedGroups[key];
    };

    return {
      cases,
      pagination,
      loadMoreCases,
      ...toRefs(state),
      onMenuItemSelected,
      openRenameDialog,
      flatCases,
      iconForItem,
      renameCase,
      containerRef,
      eventBus,
      searchQuery,
      rangePickerValue,
      datePresets,
      datePickerOpen,
      canFilterByUser,
      userPickerOpen,
      selectedUser,
      userOptions,
      usersLoading,
      filterUserOption,
      onUserFilterChange,
      onRangePickerChange,
      onSearchSubmit,
      hasResults,
      hasActiveFilter,
      hasActiveDateFilter,
      formatAbsoluteDate,
      formatRelativeDate,
      formatGroupLabel,
      isGroupCollapsed,
      toggleGroup,
      dayjs,
    };
  },
});
</script>

<style scoped>
.llm_doc_browser {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 12px 12px;
  background-color: transparent;
  overflow: hidden;
}

.llm_doc_browser_collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 12px 0;
  overflow: hidden;
}

.llm_collapsed_hint {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.35);
  animation: llm_collapsed_hint_pulse 2s ease-in-out infinite;
}

@keyframes llm_collapsed_hint_pulse {
  0%,
  100% {
    opacity: 0.4;
    transform: translateX(0);
  }
  50% {
    opacity: 0.85;
    transform: translateX(2px);
  }
}

.llm_sidebar_toolbar {
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.llm_sidebar_search {
  flex: 1 1 auto;
  min-width: 0;
}

.llm_sidebar_search :deep(.ant-input-affix-wrapper) {
  border-radius: 8px !important;
  background-color: #fff;
  height: 36px;
}

.llm_sidebar_date_row {
  padding-top: 8px;
}

.llm_sidebar_range_picker {
  width: 100%;
  border-radius: 8px !important;
}

.llm_sidebar_range_picker :deep(.ant-picker) {
  border-radius: 8px !important;
  background-color: #fff;
}

.llm_sidebar_user_select {
  width: 100%;
}

.llm_sidebar_user_select :deep(.ant-select-selector) {
  border-radius: 8px !important;
  background-color: #fff !important;
}

.llm_sidebar_date_btn {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0 !important;
  border-radius: 8px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.llm_sidebar_body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-top: 12px;
  scrollbar-width: thin;
}

.llm_group {
  margin-bottom: 8px;
}

.llm_group_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 4px;
  cursor: pointer;
  user-select: none;
}

.llm_group_label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.45);
}

.llm_group_chevron {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.4);
}

.llm_group_items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
}

.llm_case_item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
  transition: background-color 0.15s ease;
}

.llm_case_item:hover {
  background-color: rgba(0, 0, 0, 0.035);
}

.llm_case_item_selected {
  background-color: rgba(24, 68, 175, 0.08);
}

.llm_case_icon {
  flex: 0 0 auto;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.55);
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 20px;
}

.llm_case_content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.llm_case_title {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.llm_case_date {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}

.llm_case_actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.llm_case_action_btn {
  padding: 0 4px !important;
  height: 28px !important;
  width: 28px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.llm_case_action_btn :deep(.anticon) {
  font-size: 16px;
}

.llm_load_more {
  width: 100%;
  margin-top: 12px;
  color: rgba(0, 0, 0, 0.65);
}
</style>

<style>
.ant-popover {
  z-index: 5000 !important;
}
</style>
